import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import {
  fetchAdminUserSessions,
  fetchAdminUserUsageStatistics,
} from '../../../services/adminService';
import { displayUserId, formatMoney, shortText } from '../adminUtils';
import {
  ADMIN_ACCENT,
  EmptyText,
  ErrorNotice,
  IconAction,
  TypeBadge,
} from '../adminUi.jsx';
import UserStatsRangeControl from './UserStatsRangeControl.jsx';
import {
  buildUsageRangeQuery,
  createDefaultCustomRange,
  shanghaiDateKey,
} from './userStatsRange.js';
import { useDelayedUnmount } from '../../../hooks/useDelayedUnmount.js';

const numberFormatter = new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2 });
const integerFormatter = new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 0 });

const numberValue = (value) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumber = (value) => numberFormatter.format(numberValue(value));
const formatInteger = (value) => integerFormatter.format(numberValue(value));

const MetricCard = ({ label, value, hint, color }) => (
  <div
    className="rounded-lg px-3.5 py-3 min-w-0"
    style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-secondary)' }}
  >
    <div className="text-label">{label}</div>
    <div className="font-mono-num text-lg leading-7 font-semibold mt-0.5 truncate" style={color ? { color } : undefined}>
      {value}
    </div>
    {hint ? <div className="text-2xs mt-0.5 truncate" style={{ color: 'var(--color-text-muted)' }}>{hint}</div> : null}
  </div>
);

const Section = ({ title, sub, children }) => (
  <section className="mt-5">
    <div className="flex items-baseline gap-2 mb-2.5">
      <h3 className="text-[13px] font-semibold" style={{ color: 'var(--color-text-primary)' }}>{title}</h3>
      {sub ? <span className="text-label">{sub}</span> : null}
    </div>
    {children}
  </section>
);

const CHART_METRICS = [
  { key: 'totalCostCny', label: '费用', format: formatMoney, color: ADMIN_ACCENT },
  { key: 'pointsSpent', label: '积分', format: formatInteger, color: 'var(--color-blue-primary)' },
  { key: 'drawCount', label: '抽卡', format: formatInteger, color: 'var(--color-purple-primary)' },
];

const DailyUsageChart = ({ rows }) => {
  const [metricKey, setMetricKey] = useState('totalCostCny');
  const metric = CHART_METRICS.find((item) => item.key === metricKey) || CHART_METRICS[0];
  const chartRows = useMemo(() => (rows || []).slice(-120), [rows]);
  const max = Math.max(...chartRows.map((row) => numberValue(row[metric.key])), 0);
  const labelStep = Math.max(1, Math.ceil(chartRows.length / 12));

  if (!chartRows.length) return <EmptyText text="该范围没有按日用量" />;

  return (
    <div className="rounded-lg p-3.5" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-secondary)' }}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="inline-flex gap-1 rounded-md p-1" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
          {CHART_METRICS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setMetricKey(item.key)}
              className="h-6 px-2.5 rounded text-2xs font-medium"
              style={metric.key === item.key
                ? { backgroundColor: 'var(--color-bg-overlay)', color: 'var(--color-text-primary)' }
                : { color: 'var(--color-text-muted)' }}
            >
              {item.label}
            </button>
          ))}
        </div>
        <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
          {rows.length > 120 ? '趋势展示最近 120 个统计日' : `${rows.length} 个统计日`}
        </span>
      </div>
      <div className="h-36 flex items-end gap-[3px]" role="img" aria-label={`每日${metric.label}趋势`}>
        {chartRows.map((row, index) => {
          const value = numberValue(row[metric.key]);
          const height = max > 0 ? Math.max(value > 0 ? 2 : 0, (value / max) * 100) : 0;
          return (
            <div key={row.date} className="group relative h-full flex-1 min-w-[2px] flex items-end">
              <div
                className="w-full rounded-t-sm transition-opacity group-hover:opacity-100"
                style={{ height: `${height}%`, backgroundColor: metric.color, opacity: 0.72 }}
              />
              <div
                className="hidden group-hover:block absolute z-20 bottom-full mb-1.5 rounded-md px-2.5 py-2 whitespace-nowrap text-2xs font-mono-num"
                style={{
                  left: index > chartRows.length * 0.75 ? 'auto' : '50%',
                  right: index > chartRows.length * 0.75 ? 0 : 'auto',
                  transform: index > chartRows.length * 0.75 ? 'none' : 'translateX(-50%)',
                  backgroundColor: 'var(--color-bg-overlay)',
                  border: '1px solid var(--color-border-primary)',
                  boxShadow: 'var(--shadow-dropdown)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                <div>{row.date}</div>
                <div className="font-semibold mt-0.5" style={{ color: metric.color }}>{metric.format(value)}</div>
              </div>
              {index % labelStep === 0 ? (
                <span
                  className="absolute top-full mt-1 font-mono-num whitespace-nowrap"
                  style={{ fontSize: 9, color: 'var(--color-text-muted)' }}
                >
                  {String(row.date).slice(5)}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="h-4" />
    </div>
  );
};

const UserUsageDetailDialog = ({ user, initialRangeKey = 'all', initialCustomRange, onClose }) => {
  const [rangeKey, setRangeKey] = useState(initialRangeKey);
  const [customRange, setCustomRange] = useState(() => initialCustomRange || createDefaultCustomRange());
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState('');
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(true);
  const dialogVisibility = useDelayedUnmount(dialogOpen, 140, 100);
  const closeRequestedRef = useRef(false);
  const usageRequestRef = useRef(0);
  const sessionRequestRef = useRef(0);
  const rangeResolution = useMemo(() => {
    try {
      return { query: buildUsageRangeQuery(rangeKey, customRange), error: '' };
    } catch (error) {
      return { query: null, error: error.message };
    }
  }, [customRange, rangeKey]);

  const prepareUsageReload = () => {
    setUsage(null);
    setUsageLoading(true);
    setUsageError('');
  };

  const handleRangeChange = (nextRange) => {
    prepareUsageReload();
    setRangeKey(nextRange);
  };

  const handleCustomRangeChange = (nextRange) => {
    prepareUsageReload();
    setCustomRange(nextRange);
  };

  const requestClose = useCallback(() => {
    closeRequestedRef.current = true;
    setDialogOpen(false);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [requestClose]);

  useEffect(() => {
    if (!dialogVisibility.mounted && closeRequestedRef.current) onClose();
  }, [dialogVisibility.mounted, onClose]);

  useEffect(() => {
    const requestId = ++usageRequestRef.current;
    let disposed = false;
    if (rangeResolution.error) return undefined;

    fetchAdminUserUsageStatistics(user.id, rangeResolution.query)
      .then((data) => {
        if (disposed || requestId !== usageRequestRef.current) return;
        setUsage(data);
      })
      .catch((error) => {
        if (disposed || requestId !== usageRequestRef.current) return;
        console.error(error);
        setUsage(null);
        setUsageError(error?.message || '用户用量加载失败');
      })
      .finally(() => {
        if (!disposed && requestId === usageRequestRef.current) setUsageLoading(false);
      });

    return () => {
      disposed = true;
    };
  }, [rangeResolution, user.id]);

  useEffect(() => {
    const requestId = ++sessionRequestRef.current;
    let disposed = false;
    fetchAdminUserSessions(user.id)
      .then((rows) => {
        if (disposed || requestId !== sessionRequestRef.current) return;
        setSessions(rows || []);
      })
      .catch((error) => {
        if (disposed || requestId !== sessionRequestRef.current) return;
        console.error(error);
        setSessions([]);
        setSessionsError(error?.message || '在线设备加载失败');
      })
      .finally(() => {
        if (!disposed && requestId === sessionRequestRef.current) setSessionsLoading(false);
      });
    return () => {
      disposed = true;
    };
  }, [user.id]);

  const summary = usage?.summary || {};
  const currentPoints = usage?.currentPoints || {};
  const daily = usage?.daily || [];
  const projects = usage?.projects || [];
  const byProvider = usage?.byProvider || [];
  const resolvedUser = usage?.user || user;
  const range = usage?.range;
  const quotaDate = currentPoints.dailyQuotaDate;
  const dailyQuotaHint = quotaDate === shanghaiDateKey()
    ? `永久 ${formatInteger(currentPoints.permanentBalance)} · 今日剩余 ${formatInteger(currentPoints.dailyRemaining)}`
    : `永久 ${formatInteger(currentPoints.permanentBalance)} · 额度快照${quotaDate ? ` ${quotaDate}` : ''} ${formatInteger(currentPoints.dailyRemaining)}`;

  if (!dialogVisibility.mounted) return null;

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center p-4 ${dialogVisibility.exiting ? 'ui-backdrop-exit' : 'ui-backdrop-enter'}`}
      style={{ backgroundColor: 'rgba(0,0,0,0.64)', zIndex: 'var(--z-modal)' }}
      onMouseDown={(event) => { if (event.target === event.currentTarget) requestClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-usage-detail-title"
        className={`rounded-xl w-full max-w-6xl max-h-[92vh] overflow-y-auto custom-scrollbar ${dialogVisibility.exiting ? 'ui-modal-exit' : 'ui-modal-enter'}`}
        style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-secondary)', boxShadow: 'var(--shadow-modal)' }}
      >
        <header
          className="sticky top-0 z-10 flex flex-wrap justify-between items-start gap-3 px-5 py-4 border-b"
          style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border-secondary)' }}
        >
          <div>
            <div className="text-label">用户用量详情</div>
            <h2 id="user-usage-detail-title" className="text-lg font-semibold mt-0.5" style={{ color: 'var(--color-text-primary)' }}>
              {displayUserId(resolvedUser.userId || resolvedUser.name || user.userId)}
            </h2>
            {range ? (
              <div className="font-mono-num text-2xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                {range.from} 至 {range.to} · {range.timezone || 'Asia/Shanghai'}
              </div>
            ) : null}
          </div>
          <div className="flex items-start gap-3">
            <UserStatsRangeControl
              rangeKey={rangeKey}
              onRangeChange={handleRangeChange}
              customRange={customRange}
              onCustomRangeChange={handleCustomRangeChange}
              compact
            />
            <IconAction title="关闭" onClick={requestClose} icon={X} />
          </div>
        </header>

        <div className="p-5">
          {rangeResolution.error || usageError ? <ErrorNotice message={rangeResolution.error || usageError} /> : null}
          {!rangeResolution.error && usageLoading ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[0, 1, 2, 3, 4, 5, 6, 7].map((index) => <div key={index} className="skeleton h-24" />)}
              </div>
              <div className="skeleton h-48" />
              <div className="skeleton h-40" />
            </div>
          ) : usage ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricCard label="历史费用" value={formatMoney(summary.totalCostCny)} hint="所选范围内成功计费任务" color={ADMIN_ACCENT} />
                <MetricCard label="消耗积分" value={formatInteger(summary.pointsSpent)} hint="真实扣除积分" color="var(--color-blue-primary)" />
                <MetricCard label="当前积分" value={formatInteger(currentPoints.balance)} hint={dailyQuotaHint} />
                <MetricCard label="生成用量" value={formatInteger(summary.taskCount)} hint={`成功 ${formatInteger(summary.successCount)} · 失败 ${formatInteger(summary.failedCount)}`} />
                <MetricCard label="抽卡次数" value={formatInteger(summary.drawCount)} hint="每次提交计 1 次，包含失败与进行中" color="var(--color-purple-primary)" />
                <MetricCard label="平均每片段抽卡" value={formatNumber(summary.averageDrawsPerSegment)} hint={`${formatInteger(summary.drawnSegmentCount)} 个已抽卡片段`} />
                <MetricCard label="图片 / 视频" value={`${formatInteger(summary.imageCount)} / ${formatInteger(summary.videoCount)}`} hint={`视频 ${formatNumber(summary.videoSeconds)} 秒`} />
                <MetricCard label="文本 / 音频" value={`${formatInteger(summary.textCount)} / ${formatInteger(summary.audioCount)}`} hint={`Token ${formatInteger(numberValue(summary.promptTokens) + numberValue(summary.completionTokens))}`} />
              </div>

              <Section title="按日趋势" sub="切换查看费用、积分或抽卡次数">
                <DailyUsageChart rows={daily} />
              </Section>

              <Section title="按日明细" sub={`${daily.length} 天有统计记录`}>
                <div className="overflow-auto max-h-72 custom-scrollbar rounded-lg border" style={{ borderColor: 'var(--color-border-secondary)' }}>
                  <table className="w-full text-sm">
                    <thead className="sticky top-0" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                      <tr className="text-label text-left">
                        <th className="font-medium p-2.5 whitespace-nowrap">日期</th>
                        <th className="font-medium p-2.5 text-right">用量</th>
                        <th className="font-medium p-2.5 text-right">费用</th>
                        <th className="font-medium p-2.5 text-right">积分</th>
                        <th className="font-medium p-2.5 text-right">抽卡</th>
                        <th className="font-medium p-2.5 text-right">片段</th>
                        <th className="font-medium p-2.5 text-right">平均</th>
                        <th className="font-medium p-2.5 text-right">成功 / 失败</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!daily.length ? (
                        <tr><td colSpan={8}><EmptyText text="暂无按日记录" /></td></tr>
                      ) : daily.map((row) => (
                        <tr key={row.date} style={{ borderTop: '1px solid var(--color-border-secondary)', contentVisibility: 'auto' }}>
                          <td className="p-2.5 font-mono-num text-xs whitespace-nowrap">{row.date}</td>
                          <td className="p-2.5 text-right font-mono-num text-xs">{formatInteger(row.taskCount)}</td>
                          <td className="p-2.5 text-right font-mono-num text-xs font-semibold" style={{ color: ADMIN_ACCENT }}>{formatMoney(row.totalCostCny)}</td>
                          <td className="p-2.5 text-right font-mono-num text-xs">{formatInteger(row.pointsSpent)}</td>
                          <td className="p-2.5 text-right font-mono-num text-xs">{formatInteger(row.drawCount)}</td>
                          <td className="p-2.5 text-right font-mono-num text-xs">{formatInteger(row.drawnSegmentCount)}</td>
                          <td className="p-2.5 text-right font-mono-num text-xs">{formatNumber(row.averageDrawsPerSegment)}</td>
                          <td className="p-2.5 text-right font-mono-num text-xs">{formatInteger(row.successCount)} / {formatInteger(row.failedCount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>

              <Section title="按项目抽卡" sub="同一用户在不同项目中的小故事板抽卡情况">
                <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--color-border-secondary)' }}>
                  <table className="w-full text-sm">
                    <thead style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                      <tr className="text-label text-left">
                        <th className="font-medium p-2.5">项目</th>
                        <th className="font-medium p-2.5 text-right">抽卡次数</th>
                        <th className="font-medium p-2.5 text-right">已抽片段</th>
                        <th className="font-medium p-2.5 text-right">平均/片段</th>
                        <th className="font-medium p-2.5 text-right">成功 / 失败</th>
                        <th className="font-medium p-2.5 text-right">进行中</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!projects.length ? (
                        <tr><td colSpan={6}><EmptyText text="该范围暂无项目抽卡记录" /></td></tr>
                      ) : projects.map((project) => (
                        <tr key={project.projectId} style={{ borderTop: '1px solid var(--color-border-secondary)' }}>
                          <td className="p-2.5 min-w-52">
                            <div className="text-xs font-medium" style={{ color: 'var(--color-text-primary)' }}>{project.projectName || `项目 ${project.projectId}`}</div>
                            <div className="font-mono-num text-2xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>ID {project.projectId}</div>
                          </td>
                          <td className="p-2.5 text-right font-mono-num text-xs font-semibold" style={{ color: 'var(--color-purple-primary)' }}>{formatInteger(project.drawCount)}</td>
                          <td className="p-2.5 text-right font-mono-num text-xs">{formatInteger(project.drawnSegmentCount)}</td>
                          <td className="p-2.5 text-right font-mono-num text-xs">{formatNumber(project.averageDrawsPerSegment)}</td>
                          <td className="p-2.5 text-right font-mono-num text-xs">{formatInteger(project.successCount)} / {formatInteger(project.failedCount)}</td>
                          <td className="p-2.5 text-right font-mono-num text-xs">{formatInteger(numberValue(project.pendingCount) + numberValue(project.processingCount))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>

              <Section title="按模型拆分" sub="费用和生成次数由服务端账本聚合">
                <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--color-border-secondary)' }}>
                  <table className="w-full text-sm">
                    <thead style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                      <tr className="text-label text-left">
                        <th className="font-medium p-2.5">类型</th>
                        <th className="font-medium p-2.5">厂商 / 模型</th>
                        <th className="font-medium p-2.5 text-right">次数</th>
                        <th className="font-medium p-2.5 text-right">视频秒数</th>
                        <th className="font-medium p-2.5 text-right">费用</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!byProvider.length ? (
                        <tr><td colSpan={5}><EmptyText text="该范围暂无模型用量" /></td></tr>
                      ) : byProvider.map((row, index) => (
                        <tr key={`${row.type}-${row.provider}-${row.model}-${row.subModel}-${index}`} style={{ borderTop: '1px solid var(--color-border-secondary)' }}>
                          <td className="p-2.5"><TypeBadge type={row.type} /></td>
                          <td className="p-2.5 min-w-64">
                            <div className="text-xs" style={{ color: 'var(--color-text-primary)' }}>{row.provider || '-'} · {row.model || '-'}</div>
                            {row.subModel ? <div className="font-mono-num text-2xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{row.subModel}</div> : null}
                          </td>
                          <td className="p-2.5 text-right font-mono-num text-xs">{formatInteger(row.generationCount)}</td>
                          <td className="p-2.5 text-right font-mono-num text-xs">{formatNumber(row.videoSeconds)}</td>
                          <td className="p-2.5 text-right font-mono-num text-xs font-semibold" style={{ color: ADMIN_ACCENT }}>{formatMoney(row.totalCostCny)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            </>
          ) : null}

          <Section title="当前在线设备" sub={sessionsLoading ? '加载中…' : `${sessions.length} 台`}>
            {sessionsError ? <ErrorNotice message={sessionsError} /> : null}
            {sessionsLoading ? (
              <div className="grid md:grid-cols-2 gap-2"><div className="skeleton h-20" /><div className="skeleton h-20" /></div>
            ) : !sessions.length ? <EmptyText text="暂无在线设备" /> : (
              <div className="grid md:grid-cols-2 gap-2">
                {sessions.map((session) => (
                  <div key={session.id} className="rounded-md p-2.5 text-xs" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-secondary)' }}>
                    <div className="font-mono-num mb-1" style={{ color: 'var(--color-text-primary)' }}>{session.ip || '-'}</div>
                    <div style={{ color: 'var(--color-text-muted)' }}>{shortText(session.deviceLabel, 120)}</div>
                    <div className="font-mono-num text-2xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                      最近活跃：{session.lastActiveAt ? new Date(session.lastActiveAt).toLocaleString() : '-'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
};

export default UserUsageDetailDialog;
